import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ImportNewModelButton from "./ImportNewModelButton";
import ModelSettingsButton from "./ModelSettingsButton";

const isRequiredInput = (inputs, name) => {
  const input = inputs?.[name];
  return input === true || input?.required === true;
};

const ModelCard = ({
  model,
  expanded,
  onToggleExpand,
  onTest,
  onRuntimeAction,
  canEdit,
  onEdit,
  onDelete,
}) => {
  const [showBuildError, setShowBuildError] = useState(false);
  const usesTextInput = isRequiredInput(model.parameter_schema?.inputs, "text");
  const imgSrc = usesTextInput ? null : model.test_images?.find(Boolean);
  const runtimeStatus = model.runtime_status || "STOPPED";
  const buildStatus = model.build_status || "PENDING";
  const isRunning = runtimeStatus === "RUNNING";
  const isTransitioning =
    runtimeStatus === "STARTING" || runtimeStatus === "STOPPING";
  const isBuildPending =
    buildStatus === "PENDING" || buildStatus === "BUILDING";
  const isBuildFailed = buildStatus === "FAILED";
  const statusLabel = isBuildPending
    ? buildStatus
    : isBuildFailed
      ? "BUILD FAILED"
      : runtimeStatus;
  const statusIcon = isBuildPending ? (
    <AutorenewIcon
      sx={{ color: "#ed6c02", animation: "spin 1.5s linear infinite" }}
    />
  ) : isBuildFailed ? (
    <CancelIcon sx={{ color: "#d32f2f" }} />
  ) : runtimeStatus === "RUNNING" ? (
    <CheckCircleIcon sx={{ color: "#33d63b" }} />
  ) : isTransitioning ? (
    <AutorenewIcon sx={{ color: "#ed6c02" }} />
  ) : (
    <StopCircleIcon sx={{ color: "#e21a1a" }} />
  );

  return (
    <Card
      elevation={0}
      sx={{
        border: 2,
        borderColor: "rgb(243, 243, 243)",
        p: 2,
        borderRadius: 6,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Left side (text and metadata) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "70%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="medium"
                sx={{ overflowWrap: "anywhere" }}
              >
                {model.name}
              </Typography>
              <Tooltip
                title={
                  isBuildFailed
                    ? "Build failed - click to view error"
                    : statusLabel
                }
              >
                <Box
                  component="span"
                  role={isBuildFailed ? "button" : "img"}
                  tabIndex={isBuildFailed ? 0 : undefined}
                  aria-label={statusLabel}
                  onClick={
                    isBuildFailed ? () => setShowBuildError(true) : undefined
                  }
                  onKeyDown={
                    isBuildFailed
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ")
                            setShowBuildError(true);
                        }
                      : undefined
                  }
                  sx={{
                    display: "inline-flex",
                    cursor: isBuildFailed ? "pointer" : "default",
                  }}
                >
                  {statusIcon}
                </Box>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: "auto",
              }}
            >
              {canEdit && onEdit && (
                <ImportNewModelButton model={model} refreshModels={onEdit} />
              )}
              {canEdit && (
                <ModelSettingsButton
                  model={model}
                  onRuntimeAction={onRuntimeAction}
                  onDelete={onDelete}
                />
              )}
            </Box>
          </Box>

          {/* Description */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {!model.info?.description
                ? "No description available."
                : expanded
                  ? model.info.description
                  : model.info.description.slice(0, 320) +
                    (model.info.description.length > 320 ? "..." : "")}
            </Typography>

            {model.info?.description && model.info.description.length > 320 && (
              <Button
                onClick={onToggleExpand}
                sx={{
                  mt: 0.5,
                  pl: 0,
                  pr: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontSize: "12px",
                  color: "black",
                  background: "none",
                  boxShadow: "none",
                  alignSelf: "flex-start",
                }}
              >
                {expanded ? "Show Less" : "Show More"}
              </Button>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onTest}
              disabled={!isRunning}
              sx={{
                mt: 2,
                ml: 0,
                textTransform: "none",
                borderRadius: 4,
                boxShadow: "none",
                alignSelf: "flex-start",
                borderRadius: "12px",
                width: "auto",
                color: "#16a4b0",
                borderColor: "#16a4b0",
                // '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' }
              }}
            >
              Test Model
            </Button>
          </Box>
        </Box>

        {/* Right side (image) */}
        {imgSrc && (
          <Box
            component="img"
            src={imgSrc}
            alt={model.name}
            sx={{
              width: 320,
              height: 210,
              borderRadius: 2,
              objectFit: "cover",
              m: 0.7,
            }}
          />
        )}
      </Box>

      <Dialog
        open={showBuildError}
        onClose={() => setShowBuildError(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Model build failed</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {model.build_error || "No build error details are available."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowBuildError(false)}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ModelCard;
